import type { ItemCatalogEntry, ActorInventoryRecord } from '../spacetime/types';
import type { EntityProfileDetail } from '../renderer/tilemap/TileMapRenderer';
import { localizeItemName } from '../i18n/content';
import { ItemGrid } from './itemGrid/ItemGrid';
import type { ItemSlot } from './itemGrid/types';
import { createWindowFrame, windowId } from './windowing';

export interface ActorInventoryWindowHandle {
  open(profile: EntityProfileDetail, rows: ActorInventoryRecord[], itemCatalog: readonly ItemCatalogEntry[]): void;
  hide(): void;
  destroy(): void;
}

export function createActorInventoryWindow(): ActorInventoryWindowHandle {
  const container = document.createElement('div');
  container.className = 'window-container';
  Object.assign(container.style, {
    position: 'fixed',
    left: '40px',
    top: '96px',
    width: '420px',
    height: '360px',
    display: 'none',
    zIndex: '6100',
  });

  const header = document.createElement('div');
  header.className = 'window-header';
  const title = document.createElement('div');
  title.className = 'window-header-title';
  title.textContent = 'Inventory';
  const resizeHandle = document.createElement('div');
  resizeHandle.className = 'window-resize-handle';
  resizeHandle.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" style="pointer-events: none;"><path d="M0 0 L10 0 L10 10 Z" fill="#9ca3af" /></svg>';
  const closeButton = document.createElement('button');
  closeButton.type = 'button';
  closeButton.textContent = '×';
  closeButton.className = 'inventory-close';
  header.appendChild(title);
  header.appendChild(resizeHandle);
  header.appendChild(closeButton);

  const content = document.createElement('div');
  content.className = 'window-content';
  Object.assign(content.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    minHeight: '0',
  });

  const subline = document.createElement('div');
  Object.assign(subline.style, {
    color: '#94a3b8',
    fontSize: '12px',
  });

  const gridHost = document.createElement('div');
  Object.assign(gridHost.style, {
    flex: '1',
    minHeight: '0',
  });

  content.appendChild(subline);
  content.appendChild(gridHost);
  container.appendChild(header);
  container.appendChild(content);
  document.body.appendChild(container);

  const frame = createWindowFrame({
    windowId: windowId('actor-inventory', 'main'),
    container,
    header,
    resizeHandle,
    closeButton,
    minWidth: 320,
    minHeight: 260,
    initialPosition: { x: 40, y: 96 },
    initialSize: { width: 420, height: 360 },
  });

  let grid: ItemGrid | null = null;

  const open = (
    profile: EntityProfileDetail,
    rows: ActorInventoryRecord[],
    itemCatalog: readonly ItemCatalogEntry[]
  ): void => {
    const itemCatalogById = new Map(itemCatalog.map((entry) => [entry.itemId, entry]));
    title.textContent = `${profile.name} Inventory`;
    subline.textContent = profile.kind === 'mob'
      ? `Total Value: ${profile.mobInventoryValue ?? 0}`
      : 'Visible inventory';

    const slots: ItemSlot[] = rows
      .slice()
      .sort((a, b) => a.slotIndex - b.slotIndex)
      .map((row) => {
        const catalog = itemCatalogById.get(row.itemId);
        const mergedMetadata = { ...(catalog?.metadata ?? {}), ...row.metadata };
        const emoji = typeof mergedMetadata.emoji === 'string' ? mergedMetadata.emoji : '📦';
        const displayName = localizeItemName(row.itemId, mergedMetadata, catalog?.displayName ?? row.itemId);
        return {
          id: String(row.slotIndex),
          className: 'inventory-slot',
          item: {
            emoji,
            displayName,
            quantity: row.quantity,
          },
        };
      });

    if (!grid) {
      grid = new ItemGrid({
        gridId: 'actor-inventory-view',
        label: 'Items',
        capacity: Math.max(20, slots.length),
        slots,
        scrollable: true,
      });
      gridHost.appendChild(grid.element);
    } else {
      grid.update(slots, Math.max(20, slots.length));
    }

    container.style.display = 'flex';
    frame.setWindowState('open');
  };

  const hide = (): void => {
    container.style.display = 'none';
    frame.setWindowState('closed');
  };

  closeButton.addEventListener('click', hide);

  return {
    open,
    hide,
    destroy: () => {
      grid?.destroy();
      frame.destroy();
      container.remove();
    },
  };
}
